import React from 'react';
import { styled } from '@compiled/react';
import { LoadingButton } from '@atlaskit/button';
import DropdownMenu, { DropdownItemRadio, DropdownItemRadioGroup } from '@atlaskit/dropdown-menu';
import ArrowDownIcon from '@atlaskit/icon/core/migration/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/core/migration/arrow-up';
import { Section } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';

import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useFilterSortingQueryParams } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/index.tsx';
import type { SortOrder } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/types.tsx';
import messages from './messages.tsx';
import type { SortByFieldOption, SortOrderOption } from './types.tsx';
import { sortByFieldOptions, sortOrderOptions, useCurrentSortOptions } from './utils.tsx';

type Props = {
	// Only for VR test
	defaultOpen?: boolean;
};

export const SortIssuesDropdown = ({ defaultOpen }: Props) => {
	const { formatMessage } = useIntl();
	const { currentSortByOption, currentSortOrderOption } = useCurrentSortOptions();
	const { setSortByParam, setSortOrderParam } = useFilterSortingQueryParams();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<DropdownMenu
			defaultOpen={defaultOpen}
			spacing="compact"
			placement="bottom-end"
			trigger={({ triggerRef, isSelected, ...providedProps }) => (
				<StyledButton
					{...providedProps}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					ref={triggerRef as React.RefObject<HTMLElement>}
					isSelected={isSelected}
					iconAfter={<SortOrderIcon order={currentSortOrderOption.id} />}
					appearance="subtle"
				>
					{`${formatMessage(messages.sortBy)}: ${formatMessage(currentSortByOption.label)}`}
				</StyledButton>
			)}
		>
			<DropdownItemRadioGroup id="sortByFields">
				{sortByFieldOptions.map((field: SortByFieldOption) => (
					<StyledDropdownItemRadio
						isSelected={field.id === currentSortByOption.id}
						id={field.id}
						key={field.id}
						onClick={() => {
							setSortByParam(field.id);
							const analyticsEvent = createAnalyticsEvent({
								action: 'toggled',
								actionSubject: 'radio',
							});
							fireUIAnalytics(analyticsEvent, 'sorting', {
								sortBy: field.id,
								sortOrder: currentSortOrderOption.id,
							});
						}}
					>
						{formatMessage(field.label)}
					</StyledDropdownItemRadio>
				))}
			</DropdownItemRadioGroup>
			<Section hasSeparator>
				<DropdownItemRadioGroup id="sortOrderOptions">
					{sortOrderOptions.map((order: SortOrderOption) => (
						<StyledDropdownItemRadio
							isSelected={order.id === currentSortOrderOption.id}
							id={order.id}
							key={order.id}
							onClick={() => {
								setSortOrderParam(order.id);
								const analyticsEvent = createAnalyticsEvent({
									action: 'toggled',
									actionSubject: 'radio',
								});
								fireUIAnalytics(analyticsEvent, 'sorting', {
									sortBy: currentSortByOption.id,
									sortOrder: order.id,
								});
							}}
						>
							{formatMessage(order.label)}
						</StyledDropdownItemRadio>
					))}
				</DropdownItemRadioGroup>
			</Section>
		</DropdownMenu>
	);
};

export const SortOrderIcon = ({ order }: { order: SortOrder }) => {
	switch (order) {
		case 'ASC':
			return (
				<ArrowUpIcon
					label="software-releases-version-detail.common.sort-issues.ui.sort-order-icon.ascending"
					LEGACY_size="small"
				/>
			);
		case 'DESC':
			return (
				<ArrowDownIcon
					label="software-releases-version-detail.common.sort-issues.ui.sort-order-icon.descending"
					LEGACY_size="small"
				/>
			);
		default:
			return (
				<ArrowDownIcon
					label="software-releases-version-detail.common.sort-issues.ui.sort-order-icon.descending"
					LEGACY_size="small"
				/>
			);
	}
};

// This component is defined here deliberately to avoid DropdownItemRadio to be included in the Version details bundle.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StyledDropdownItemRadio = styled(DropdownItemRadio)({
	fontWeight: token('font.weight.regular'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&& > span': {
		marginRight: token('space.050'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StyledButton = styled(LoadingButton)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		display: 'flex',
		alignItems: 'center',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
	},
});
